import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { Button } from 'react-style-guide';
import { DeviceMeta, EmailVerificationService, PremiumDisclosures } from 'Roblox';
import PremiumConstants from '../constants/PremiumConstants';
import PremiumService from '../services/premiumService';
import isVerificationUpsellEnabled from '../utilities/isVerificationUpsellModalEnabled';

const { isIosApp, isAndroidApp, isUWPApp, isAmazonApp } = DeviceMeta();
function SubscriptionCard({ translate, intl, planInfo, userPlan }) {
  const [cardStatus, setCardStatus] = useState(null);

  const planTitle = translate('Label.PremiumPlanName', { type: planInfo.robuxAmount });
  const amountStr = `${planInfo.price.currency.currencySymbol}${planInfo.price.amount}`;
  const priceLabel = () => {
    // for recurring subscription, we show "price/mo"
    if (planInfo.isRecurring) {
      return translate('Label.AmountPerMonth', {
        amount: amountStr,
        subTextStart: '<span class="font-bold">',
        subTextEnd: '</span>'
      });
    }
    return amountStr; // non-recurring, just return price.amount
  };

  const packageDescription = () => {
    const robuxElement = `<span class='icon-robux-16x16'></span><span>${planInfo.robuxAmount}</span>`;
    if (planInfo.isRecurring) {
      return translate('Description.GetRobuxEachMonth', {
        robux: robuxElement
      });
    }
    return translate('Description.GetRobux', {
      robux: robuxElement
    });
  };

  const redirectToMobilePurchase = () => {
    const link = document.createElement('a');
    link.href = PremiumService.getPaymentUrl(planInfo);
    link.click();
  };

  const showPremiumDisclosure = () => {
    const now = new Date();
    const renewDate = new Date(now.setMonth(now.getMonth() + 1));
    PremiumDisclosures.PremiumDisclosuresService.show(
      planInfo.name,
      translate('Label.Monthly'),
      renewDate,
      amountStr,
      redirectToMobilePurchase
    );
  };

  const purchasePlan = () => {
    if (isIosApp || isAndroidApp || isUWPApp || isAmazonApp) {
      // for inApp payment prompt to work properly, a new a tag is created.
      if (planInfo.isRecurring) {
        showPremiumDisclosure();
      } else {
        redirectToMobilePurchase();
      }
    } else {
      // universal desktop app, redirect to payment method page
      const buyUrl = PremiumService.getPaymentUrl(planInfo);
      window.location.href = buyUrl;
    }
  };

  const onSubscribeButtonClick = e => {
    e.preventDefault();
    if (cardStatus?.buttonDisabled) {
      return;
    }
    PremiumService.sendInteractionClickEvent(
      PremiumConstants.premiumFeatureType.subscription,
      PremiumConstants.streamEventParams.buyBtn,
      planInfo.productId
    );

    // To show verification upsell modal
    if (isVerificationUpsellEnabled()) {
      EmailVerificationService?.handleUserEmailUpsellAtPremiumSubscription(() => {
        purchasePlan();
      }).then(data => {
        if (data?.emailAddress) {
          purchasePlan();
        }
      });
    } else {
      purchasePlan();
    }
  };

  useEffect(() => {
    const status = {
      dateString: '',
      cssClass: 'subscription-card',
      buttonLabelKey: 'Label.SubscribeNow',
      buttonDisabled: false
    };

    const getUserPlanDate = () => {
      return userPlan.renewal ? translate('Label.AutoRenewal') : translate('Label.CurrentPlan');
    };

    if (userPlan) {
      if (userPlan.premiumFeatureId === planInfo.premiumFeatureId) {
        status.dateString = getUserPlanDate();
        status.cssClass = 'subscription-card purchased';
        status.buttonLabelKey = 'Action.CurrentPlan';
        status.buttonDisabled = true;
      } else {
        status.cssClass = 'subscription-card disabled';
        status.buttonLabelKey = 'Action.NotAvailable';
        status.buttonDisabled = true;
      }
    }
    setCardStatus(status);
  }, [intl, planInfo.premiumFeatureId, translate, userPlan]);

  return (
    <div className='div-table-cell'>
      {cardStatus?.dateString && (
        <div className='font-header-2 plan-date'>{cardStatus.dateString}</div>
      )}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href='#'
        className={cardStatus?.buttonDisabled && 'disabled'}
        onClick={e => onSubscribeButtonClick(e)}>
        <div className={cardStatus?.cssClass}>
          <div className='medium-header plan-name'>{planTitle}</div>
          <div className='icon-premium-88x88' />
          <div className='subscription-price'>
            <div className='font-title'>
              <span dangerouslySetInnerHTML={{ __html: priceLabel() }} />
            </div>
          </div>
          <div dangerouslySetInnerHTML={{ __html: packageDescription() }} />
          <Button
            className='subscribe-button'
            variant={Button.variants.primary}
            width={Button.widths.min}
            size={Button.sizes.large}
            isDisabled={cardStatus?.buttonDisabled}>
            {cardStatus && translate(cardStatus.buttonLabelKey)}
          </Button>
        </div>
      </a>
    </div>
  );
}

SubscriptionCard.defaultProps = {
  userPlan: null
};

SubscriptionCard.propTypes = {
  translate: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    d: PropTypes.func.isRequired
  }).isRequired,
  planInfo: PropTypes.shape({
    productId: PropTypes.number.isRequired,
    premiumFeatureId: PropTypes.number.isRequired,
    robuxAmount: PropTypes.string.isRequired,
    isRecurring: PropTypes.bool.isRequired,
    price: PropTypes.shape({
      amount: PropTypes.number.isRequired,
      currency: PropTypes.shape({
        currencySymbol: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  userPlan: PropTypes.shape({
    premiumFeatureId: PropTypes.number.isRequired,
    subscriptionTypeName: PropTypes.string.isRequired,
    robuxStipendAmount: PropTypes.number.isRequired,
    isLifetime: PropTypes.bool.isRequired,
    expiration: PropTypes.string,
    renewal: PropTypes.string,
    created: PropTypes.string.isRequired,
    purchasePlatform: PropTypes.string.isRequired
  })
};

export default SubscriptionCard;
